import React, { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import ExtendSubmissionDeadlineModal from "pages/RFQ/RFQ/RFQDetail/RFQUpdateSubmissionDeadline";
import type { RfxViewMode } from "../../hooks/useRfxViewMode";
import type { RfxListPageMeta } from "../../hooks/useRfxList";
import type { RfxListTab } from "../../types/rfx-list.type";
import type { Rfx } from "../../types/rfx.types";
import RFQGridView from "../legacy/RFQGridView";
import RFQListView from "../legacy/RFQListView";

export interface TendersQuotesListProps {
	meta: RfxListPageMeta | undefined;
	rows: Rfx[];
	/**
	 * TASK-036 (typecheck sweep): active tab, threaded to the legacy views so
	 * their `currentTab !== "archived"` extend-action gate works (legacy
	 * `RFQ.js` parity — was silently undefined since TASK-027's restoration).
	 */
	currentTab?: RfxListTab;
	/**
	 * Grid/list mode, owned by `RfxListPage`'s toolbar (legacy `RFQ.js`
	 * parity). This component no longer renders its own `ButtonViewGroup` —
	 * doing so put the toggle on a fourth stacked row below the tabs.
	 */
	view: RfxViewMode;
}

/**
 * Procurer Tenders & Quotes row rendering — plugs into 007's `RfxListPage`
 * `renderList` slot (only invoked for non-empty `rows`; the tab-specific
 * empty copy lives in `TendersQuotesEmptyState`, passed as
 * `RfxListPage`'s `renderEmptyState`). Preserves legacy view/edit link
 * targets (`clientPaths.viewRfx`/`editRfx`) unchanged; drafts link to Edit,
 * everything else links to View. Server-authoritative: rows render exactly
 * what `useRfxList` returned for the active tab, no client-side re-filtering.
 *
 * TASK-027: restores the legacy card/list toggle VERBATIM — `RFQ.js`'s
 * `ButtonViewGroup` (already reused, never deleted) driving the real
 * `RFQGridView`/`RFQListView`/`RFQCard` restored under `components/legacy/`
 * (deleted by Task 008 @ 310ebe2d4, resurrected here), instead of Task 023's
 * lookalike `TendersQuotesCard`/inline table (now deleted). Only the data
 * source (react-query `rows` instead of the legacy `state.rfq.rfqs` redux
 * slice) and the row-action menu (TASK-018's `RfxRowActions`, status-driven,
 * instead of legacy's tab-keyed `EditDropdown`) were swapped — see the
 * header comments on the restored legacy files and TASK_SUMMARY-027.md for
 * the full accounting. `renderList` (007's `RfxListPage`) only invokes this
 * once loading has settled, so the legacy skeleton branches are inert.
 */
export const TendersQuotesList = ({
	rows,
	currentTab = "current",
	view,
}: TendersQuotesListProps) => {
	const queryClient = useQueryClient();
	const [extendTarget, setExtendTarget] = useState<Rfx | null>(null);

	const extendModal = extendTarget && (
		<ExtendSubmissionDeadlineModal
			isModalOpen={!!extendTarget}
			rfqDetail={extendTarget}
			onClose={() => setExtendTarget(null)}
			onSubmissionDateExtension={() => {
				queryClient.invalidateQueries({ queryKey: ["rfx-list"] });
				setExtendTarget(null);
			}}
		/>
	);

	return (
		<div data-testid="tenders-quotes-list">
			{view === "grid" ? (
				<div data-testid="tenders-quotes-grid">
					<RFQGridView
						rows={rows}
						isLoading={false}
						currentTab={currentTab}
						onExtendClick={setExtendTarget}
						isSidebarCollapsed={false}
					/>
				</div>
			) : (
				<div data-testid="tenders-quotes-table">
					<RFQListView
						rows={rows}
						isLoading={false}
						currentTab={currentTab}
						onExtendClick={setExtendTarget}
					/>
				</div>
			)}

			{extendModal}
		</div>
	);
};
